import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import {
  getGetSpotsQueryKey,
  getListBookingsQueryKey,
  getQparkStatusQueryKey,
  getPlateSyncStatusQueryKey,
  getSpotsQueryKey,
  getStatusQueryKey,
  useAdminDeletePlate,
  useGetSpots,
  useListQparkPlates,
  useListRights,
  useMe,
  usePlateSyncRun,
  usePlateSyncStatus,
  usePutSpots,
  useQparkDisconnect,
  useQparkStatus,
  useReconcileNow,
} from '@api/generated'
import type { SpotDto, SyncRow, SyncStatus } from '@api/generated/models'
import { ConnectCard } from '@components/ConnectCard'
import { PageSpinner } from '@components/PageSpinner'
import { errorMessage } from '@utils/errors'

export const Route = createFileRoute('/admin')({ component: Admin })

function Admin() {
  const me = useMe()
  if (me.isPending) return <PageSpinner />
  if (!me.data?.admin) {
    return (
      <div role="alert" className="alert alert-error mx-4 sm:mx-8">
        Nur für Administratoren.
      </div>
    )
  }
  return (
    <div className="px-4 sm:px-8 space-y-6 sm:space-y-8 max-w-4xl">
      <h1 className="text-2xl sm:text-3xl font-bold">Administration</h1>
      <Connection />
      <Settings />
      <PlateSync />
    </div>
  )
}

function Connection() {
  const queryClient = useQueryClient()
  const status = useQparkStatus()
  const disconnect = useQparkDisconnect({
    mutation: {
      onSuccess: () =>
        Promise.all([
          queryClient.invalidateQueries({ queryKey: getStatusQueryKey() }),
          queryClient.invalidateQueries({ queryKey: getQparkStatusQueryKey() }),
        ]),
    },
  })
  if (status.isPending) return <PageSpinner inline />
  if (!status.data?.connected) return <ConnectCard />
  return (
    <div className="card bg-base-200">
      <div className="card-body flex-col items-stretch gap-4 sm:flex-row sm:items-center">
        <div className="flex-1">
          <h2 className="card-title">Q-Park-Konto</h2>
          <p className="opacity-70">Verbunden als {status.data.account}</p>
        </div>
        <button
          type="button"
          className="btn btn-outline btn-error"
          disabled={disconnect.isPending}
          onClick={() => {
            if (
              confirm(
                'Q-Park-Konto trennen? Buchungen funktionieren dann nicht mehr.',
              )
            ) {
              disconnect.mutate()
            }
          }}
        >
          Trennen
        </button>
      </div>
    </div>
  )
}

function Settings() {
  const spots = useGetSpots()
  const rights = useListRights()
  if (spots.isPending || rights.isPending) return <PageSpinner inline />
  if (spots.isError) {
    return (
      <div role="alert" className="alert alert-error">
        {errorMessage(spots.error)}
      </div>
    )
  }
  return (
    <div className="space-y-3">
      {rights.isError && (
        <div role="alert" className="alert alert-warning alert-soft text-sm">
          Die Parkberechtigungen konnten nicht von Q-Park geladen werden (
          {errorMessage(rights.error)}). Bezeichnungen und Ersatzkennzeichen
          bestehender Stellplätze lassen sich weiterhin bearbeiten.
        </div>
      )}
      <SpotsForm spots={spots.data} rights={rights.data ?? []} />
    </div>
  )
}

type Right = {
  id: string
  location?: string | null
  product?: string | null
  plate?: string | null
}
type Row = {
  key: string
  rightId: string
  label: string
  fallbackPlate: string
  fallbackCountry: string
}

const toRows = (list: SpotDto[]): Row[] =>
  list.map((s) => ({
    key: s.id,
    rightId: s.rightId,
    label: s.label,
    fallbackPlate: s.fallbackPlate ?? '',
    fallbackCountry: s.fallbackCountry,
  }))

function SpotsForm({ spots, rights }: { spots: SpotDto[]; rights: Right[] }) {
  const queryClient = useQueryClient()
  const qparkPlates = useListQparkPlates()
  const [rows, setRows] = useState<Row[]>(() => toRows(spots))
  const save = usePutSpots({
    mutation: {
      onSuccess: (saved) => {
        setRows(toRows(saved)) // show what the server stored (labels, order) without remounting
        return Promise.all([
          queryClient.invalidateQueries({ queryKey: getGetSpotsQueryKey() }),
          queryClient.invalidateQueries({ queryKey: getSpotsQueryKey() }),
          queryClient.invalidateQueries({
            queryKey: getListBookingsQueryKey(),
          }),
        ])
      },
    },
  })
  const apply = useReconcileNow({
    mutation: { onSuccess: () => queryClient.invalidateQueries() },
  })

  const update = (i: number, patch: Partial<Row>) =>
    setRows(rows.map((r, n) => (n === i ? { ...r, ...patch } : r)))
  const move = (i: number, delta: number) => {
    const j = i + delta
    if (j < 0 || j >= rows.length) return
    const next = [...rows]
    ;[next[i], next[j]] = [next[j], next[i]]
    setRows(next)
  }
  const used = new Set(rows.map((r) => r.rightId))
  const rightName = (r: Right) =>
    `${[r.location, r.product].filter(Boolean).join(' · ')} (aktuell: ${r.plate ?? 'kein Kennzeichen'})`

  return (
    <form
      className="card bg-base-200 card-body gap-4 p-4 sm:p-6"
      onSubmit={(e) => {
        e.preventDefault()
        save.mutate({
          data: rows.map((r) => ({
            rightId: r.rightId,
            label: r.label,
            fallbackPlate: r.fallbackPlate || null,
            fallbackCountry: r.fallbackCountry,
          })),
        })
      }}
    >
      <h2 className="card-title">Stellplätze</h2>
      <p className="text-sm opacity-70">
        Jeder Stellplatz entspricht einer Parkberechtigung im Q-Park-Konto. Eine
        Tageszelle zeigt pro Stellplatz einen Abschnitt, in dieser Reihenfolge.
        Außerhalb von Buchungen erhält ein Stellplatz sein Ersatzkennzeichen;
        bleibt es leer, bleibt das aktuell hinterlegte Kennzeichen bestehen.
      </p>

      {rows.length === 0 && (
        <p className="opacity-60">
          Noch keine Stellplätze. Unten kann einer hinzugefügt werden.
        </p>
      )}
      {rows.map((r, i) => (
        <div
          key={r.key}
          className="rounded-box bg-base-100 p-3 space-y-3"
          data-spot-row={i + 1}
        >
          <div className="flex flex-wrap items-center gap-2">
            <span className="badge badge-primary">{i + 1}</span>
            <input
              className="input input-bordered input-sm min-w-32 flex-1"
              placeholder={`Stellplatz ${i + 1}`}
              aria-label="Bezeichnung"
              value={r.label}
              onChange={(e) => update(i, { label: e.target.value })}
            />
            <button
              type="button"
              className="btn btn-sm btn-ghost btn-square"
              aria-label="Nach oben"
              disabled={i === 0}
              onClick={() => move(i, -1)}
            >
              ↑
            </button>
            <button
              type="button"
              className="btn btn-sm btn-ghost btn-square"
              aria-label="Nach unten"
              disabled={i === rows.length - 1}
              onClick={() => move(i, 1)}
            >
              ↓
            </button>
            <button
              type="button"
              className="btn btn-sm btn-error btn-outline"
              onClick={() => {
                if (confirm(`${r.label || `Stellplatz ${i + 1}`} entfernen?`)) {
                  setRows(rows.filter((_, n) => n !== i))
                }
              }}
            >
              Entfernen
            </button>
          </div>
          <select
            className="select select-bordered select-sm w-full"
            aria-label="Parkberechtigung"
            value={r.rightId}
            onChange={(e) => update(i, { rightId: e.target.value })}
          >
            {!rights.some((x) => x.id === r.rightId) && (
              <option value={r.rightId}>
                {spots.find((s) => s.rightId === r.rightId)?.rightLabel ??
                  r.rightId}
              </option>
            )}
            {rights
              .filter((x) => x.id === r.rightId || !used.has(x.id))
              .map((x) => (
                <option key={x.id} value={x.id}>
                  {rightName(x)}
                </option>
              ))}
          </select>
          <p className="text-sm">
            <span className="opacity-70">Aktuell in der Q-Park-App: </span>
            {(() => {
              const plate = rights.find((x) => x.id === r.rightId)?.plate
              return plate ? (
                <span className="badge badge-neutral font-bold tracking-wide">
                  {plate}
                </span>
              ) : (
                <span className="opacity-60">kein Kennzeichen</span>
              )
            })()}
          </p>
          <div className="grid gap-3 sm:grid-cols-[3fr_1fr]">
            <input
              className="input input-bordered input-sm w-full uppercase"
              list="qpark-plates"
              aria-label="Ersatzkennzeichen"
              placeholder="Ersatzkennzeichen, z. B. MSP-MG278"
              value={r.fallbackPlate}
              onChange={(e) => update(i, { fallbackPlate: e.target.value })}
            />
            <input
              className="input input-bordered input-sm w-full uppercase"
              aria-label="Land des Ersatzkennzeichens"
              maxLength={2}
              required
              value={r.fallbackCountry}
              onChange={(e) => update(i, { fallbackCountry: e.target.value })}
            />
          </div>
        </div>
      ))}
      <datalist id="qpark-plates">
        {qparkPlates.data?.map((p) => (
          <option key={p.plate} value={p.plate}>
            {p.name}
          </option>
        ))}
      </datalist>

      {(() => {
        const free = rights.find((x) => !used.has(x.id))
        return (
          <div>
            <button
              type="button"
              className="btn btn-sm btn-outline"
              disabled={!free || rows.length >= 6}
              title={
                free
                  ? undefined
                  : 'Keine freie Parkberechtigung im Q-Park-Konto'
              }
              onClick={() =>
                free &&
                setRows([
                  ...rows,
                  {
                    key: crypto.randomUUID(),
                    rightId: free.id,
                    label: '',
                    fallbackPlate: '',
                    fallbackCountry: 'de',
                  },
                ])
              }
            >
              + Stellplatz hinzufügen
            </button>
          </div>
        )
      })()}

      {save.isError && (
        <div role="alert" className="alert alert-error alert-soft text-sm">
          {errorMessage(save.error)}
        </div>
      )}
      {save.isSuccess && (
        <div role="alert" className="alert alert-success alert-soft text-sm">
          Gespeichert.
        </div>
      )}
      <div className="flex flex-wrap gap-3">
        <button
          className="btn btn-primary"
          type="submit"
          disabled={save.isPending}
        >
          {save.isPending && <span className="loading loading-spinner" />}
          Speichern
        </button>
        <button
          type="button"
          className="btn btn-outline"
          disabled={apply.isPending || spots.length === 0}
          onClick={() => apply.mutate()}
          title="Buchungen jetzt prüfen und bei jedem Stellplatz mit falschem Kennzeichen korrigieren"
        >
          {apply.isPending && <span className="loading loading-spinner" />}
          Jetzt anwenden
        </button>
      </div>
      {apply.isError && (
        <div role="alert" className="alert alert-error alert-soft text-sm">
          {errorMessage(apply.error)}
        </div>
      )}
      {apply.isSuccess && (
        <ul className="space-y-1 text-sm">
          {apply.data.map((r) => (
            <li
              key={r.spotId}
              className={`alert alert-soft py-2 ${r.error ? 'alert-error' : 'alert-info'}`}
            >
              <span>
                <b>{r.label}:</b>{' '}
                {r.error
                  ? `fehlgeschlagen (${r.error}), wird automatisch erneut versucht.`
                  : r.desired
                    ? `soll ${r.desired} sein (${r.note}). ${
                        r.outcome
                          ? r.outcome.changed
                            ? `Umgestellt${r.outcome.verified ? ' und bestätigt' : ', noch nicht bestätigt'}.`
                            : 'Bereits korrekt.'
                          : 'Nichts zu tun.'
                      }`
                    : 'nichts anzuwenden: keine Buchung und kein Ersatzkennzeichen.'}
              </span>
            </li>
          ))}
        </ul>
      )}
    </form>
  )
}

const STATUS: Record<
  SyncStatus,
  { label: string; badge: string; hint: string }
> = {
  in_sync: {
    label: 'synchron',
    badge: 'badge-success',
    hint: 'Hier und in Q-Park registriert',
  },
  missing_in_qpark: {
    label: 'fehlt in Q-Park',
    badge: 'badge-warning',
    hint: 'Wird bei der nächsten Synchronisierung zu Q-Park hinzugefügt',
  },
  orphan: {
    label: 'verwaist',
    badge: 'badge-warning',
    hint: 'Von parktool hinzugefügt, hat keinen Besitzer mehr; wird bei der nächsten Synchronisierung entfernt',
  },
  in_use: {
    label: 'in Benutzung',
    badge: 'badge-info',
    hint: 'Kein Besitzer, ist aber ein Ersatzkennzeichen oder aktuell einem Stellplatz zugewiesen',
  },
  external: {
    label: 'nicht verwaltet',
    badge: 'badge-ghost',
    hint: 'Von jemand anderem in Q-Park gespeichert; parktool lässt es unberührt',
  },
}

function PlateSync() {
  const queryClient = useQueryClient()
  const status = usePlateSyncStatus()
  const sync = usePlateSyncRun({
    mutation: {
      onSuccess: () =>
        queryClient.invalidateQueries({
          queryKey: getPlateSyncStatusQueryKey(),
        }),
    },
  })
  const del = useAdminDeletePlate({
    mutation: {
      onSuccess: () =>
        queryClient.invalidateQueries({
          queryKey: getPlateSyncStatusQueryKey(),
        }),
    },
  })
  const confirmDelete = (r: SyncRow) => {
    const bookingWarning =
      r.upcomingBookings > 0
        ? `\n\nAchtung: ${r.upcomingBookings === 1 ? 'Eine kommende Buchung wird' : `${r.upcomingBookings} kommende Buchungen werden`} ebenfalls storniert.`
        : ''
    const text =
      r.status === 'external'
        ? `${r.plate} („${r.qparkName}“) aus der Q-Park-Fahrzeugliste entfernen?\n\nparktool verwaltet dieses Fahrzeug nicht: Es wurde direkt in Q-Park gespeichert. Dadurch wird es im echten Q-Park-Konto gelöscht.`
        : r.ownerName
          ? `${r.plate}, registriert von ${r.ownerName}, löschen?${bookingWarning}`
          : `${r.plate} aus der Q-Park-Fahrzeugliste entfernen?`
    if (confirm(text)) del.mutate({ country: r.country, plate: r.plate })
  }
  const drift = status.data?.filter(
    (r) => r.status === 'missing_in_qpark' || r.status === 'orphan',
  ).length

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="text-xl font-bold flex-1 basis-48">
          Kennzeichen: parktool vs. Q-Park
        </h2>
        <button
          type="button"
          className="btn btn-sm btn-outline"
          disabled={sync.isPending}
          onClick={() => sync.mutate()}
          title="In Q-Park fehlende Kennzeichen hinzufügen und Überreste von parktool dort entfernen"
        >
          {sync.isPending && (
            <span className="loading loading-spinner loading-xs" />
          )}
          Jetzt synchronisieren
        </button>
      </div>
      <p className="text-sm opacity-70">
        Beim Registrieren eines Kennzeichens wird es sofort zur Q-Park-
        Fahrzeugliste hinzugefügt (benannt nach dem Namen des Kennzeichens oder
        dem Benutzernamen), beim Löschen wird es wieder entfernt. Ein
        Hintergrundjob gleicht Abweichungen zusätzlich alle 30 Minuten ab.
        Fahrzeuge, die du selbst in Q-Park gespeichert hast, bleiben unberührt.
      </p>
      {status.isPending && <PageSpinner inline />}
      {status.isError && (
        <div role="alert" className="alert alert-warning alert-soft text-sm">
          Vergleich mit Q-Park nicht möglich: {errorMessage(status.error)}
        </div>
      )}
      {sync.isError && (
        <div role="alert" className="alert alert-error alert-soft text-sm">
          {errorMessage(sync.error)}
        </div>
      )}
      {del.isError && (
        <div role="alert" className="alert alert-error alert-soft text-sm">
          {errorMessage(del.error)}
        </div>
      )}
      {sync.isSuccess && (
        <div role="alert" className="alert alert-info alert-soft text-sm">
          {sync.data.added.length} hinzugefügt, {sync.data.removed.length}{' '}
          entfernt
          {sync.data.errors.length > 0 &&
            `, ${sync.data.errors.length} fehlgeschlagen: ${sync.data.errors.join('; ')}`}
          .
        </div>
      )}
      {drift !== undefined && drift > 0 && !sync.isPending && (
        <div role="alert" className="alert alert-warning alert-soft text-sm">
          {drift} {drift === 1 ? 'Kennzeichen ist' : 'Kennzeichen sind'} nicht
          synchron.
        </div>
      )}
      {status.data && (
        <div className="overflow-x-auto rounded-box bg-base-200">
          <table className="table table-sm sm:table-md">
            <thead>
              <tr>
                <th>Kennzeichen</th>
                <th>Registriert von</th>
                <th className="max-sm:hidden">Name in Q-Park</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {status.data.map((r) => {
                const st = STATUS[r.status]
                return (
                  <tr key={`${r.country}-${r.plate}`}>
                    <td className="font-bold tracking-wide">{r.plate}</td>
                    <td>
                      {r.ownerName ?? <span className="opacity-50">–</span>}
                    </td>
                    <td className="max-sm:hidden">
                      {r.qparkName ?? <span className="opacity-50">–</span>}
                    </td>
                    <td>
                      <span className={`badge ${st.badge}`} title={st.hint}>
                        {st.label}
                      </span>
                    </td>
                    <td className="text-right">
                      <button
                        type="button"
                        className="btn btn-xs btn-error btn-outline"
                        disabled={del.isPending || r.status === 'in_use'}
                        title={
                          r.status === 'in_use'
                            ? 'Ersatzkennzeichen oder einem Stellplatz zugewiesen: zuerst dort ändern'
                            : 'Löschen'
                        }
                        onClick={() => confirmDelete(r)}
                      >
                        {del.isPending && del.variables?.plate === r.plate && (
                          <span className="loading loading-spinner loading-xs" />
                        )}
                        Löschen
                      </button>
                    </td>
                  </tr>
                )
              })}
              {status.data.length === 0 && (
                <tr>
                  <td colSpan={5} className="text-center opacity-60">
                    Noch keine Kennzeichen vorhanden
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
